import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { AuthUser } from '@fieldforce/shared';
import { authApi, getStoredUser, getAuthToken, clearAuthData } from '@/lib/api';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSalesStaff: boolean;
  isLoading: boolean;
  login: (mobile: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Session timeout: 14 hours for all users
const SESSION_TIMEOUT = 14 * 60 * 60 * 1000;
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check every minute

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Track user activity
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  // Check session validity
  useEffect(() => {
    const checkSession = () => {
      if (!user) return;

      const inactiveTime = Date.now() - lastActivity;
      if (inactiveTime > SESSION_TIMEOUT) {
        logout();
      }
    };

    const interval = setInterval(checkSession, ACTIVITY_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [user, lastActivity, logout]);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      const storedUser = getStoredUser();

      if (!token || !storedUser) {
        setIsLoading(false);
        return;
      }

      // Verify token with server
      const result = await authApi.verify();

      if (result.success && result.data) {
        setUser(result.data.user);
      } else {
        // Token invalid, clear storage
        clearAuthData();
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (mobile: string, password: string) => {
    const result = await authApi.login(mobile, password);

    if (result.success && result.data) {
      setUser(result.data.user);
      setLastActivity(Date.now());
      return { success: true };
    }

    return {
      success: false,
      error: result.error?.message || 'Login failed',
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isSalesStaff: user?.role === 'sales_staff',
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
